import React, { useEffect, useRef, useState } from 'react';
import classnames from 'classnames';
import { useNavigate } from 'react-router-dom';
import { DicomMetadataStore, MODULE_TYPES } from '@ohif/core';

import Dropzone from 'react-dropzone';
import filesToStudies from './filesToStudies';

import { extensionManager } from '../../App';
import { Icon, LoadingIndicatorProgress } from '@ohif/ui';
import axios from 'axios';
import { CLIENT_NAME, LocalAPI, LocalAPIUpload } from '../../../../../constants/baseurl';

type LocalProps = {
  modePath: string;
};

function Local({ modePath }: LocalProps) {
  const navigate = useNavigate();
  const dropzoneRef = useRef<any>();
  const [dropInitiated, setDropInitiated] = useState(false);
  const [serverFS, setServerFS] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadTotal, setUploadTotal] = useState(0);
  const [uploadCurrent, setUploadCurrent] = useState(0);
  const [uploadError, setUploadError] = useState('');

  // Initializing the dicom local dataSource
  const dataSourceModules = extensionManager.modules[MODULE_TYPES.DATA_SOURCE] as any[];
  const localDataSources = dataSourceModules.reduce((acc: any[], curr: any) => {
    const mods: any[] = [];
    curr.module.forEach((mod: any) => {
      if (mod.type === 'localApi') {
        mods.push(mod);
      }
    });
    return acc.concat(mods);
  }, []);

  const firstLocalDataSource = localDataSources[0];
  const dataSource = firstLocalDataSource.createDataSource({});

  const microscopyExtensionLoaded = extensionManager.registeredExtensionIds.includes(
    '@dcm-cloud/extension-dicom-microscopy'
  ) as boolean;

  useEffect(() => {
    if (CLIENT_NAME === 'demo-local-fs') {
      setServerFS(true);
    }
  }, []);

  // Function to upload a single file in chunks
  const uploadFileInChunks = async (
    file: File,
    index: number,
    totalFiles: number,
    existingStudyUID: string = ''
  ) => {
    const CHUNK_SIZE = 10 * 1024 * 1024; // 10MB chunks (smaller to avoid 413 errors)
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    const fileName = file.name;

    setUploadCurrent(index + 1);
    setUploadTotal(totalFiles);

    try {
      let fileId = null;

      // For files smaller than the chunk size, use direct upload
      if (file.size <= CHUNK_SIZE) {
        const token = localStorage.getItem('token');
        const headers = {
          'Content-Type': file.type || 'application/octet-stream',
          Authorization: `Bearer ${token}`,
        };

        // Add study UID if we have one from previous uploads
        if (existingStudyUID) {
          headers['X-Study-Instance-UID'] = existingStudyUID;
        }

        // Use the direct_upload endpoint for whole file
        const response = await axios.put(
          `${LocalAPIUpload}/direct_upload/${encodeURIComponent(fileName)}`,
          file,
          {
            headers,
            onUploadProgress: (progressEvent: any) => {
              const percentCompleted = Math.round(
                (progressEvent.loaded / progressEvent.total) * 100
              );
              setUploadProgress(percentCompleted);
            },
          }
        );

        setUploadProgress(100);

        // If this is the first file and we got a StudyInstanceUID back, return it
        if (index === 0 && response.data && response.data.studyUID) {
          return response.data.studyUID;
        }

        return true;
      }

      // For larger files, upload in chunks
      for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
        const start = chunkIndex * CHUNK_SIZE;
        const end = Math.min(file.size, start + CHUNK_SIZE);
        const chunk = file.slice(start, end);

        // Create a unique filename for each chunk that shows it's part of a sequence
        const chunkFileName = `${fileName}.part${chunkIndex + 1}of${totalChunks}`;

        const token = localStorage.getItem('token');
        const headers = {
          'Content-Type': 'application/octet-stream',
          Authorization: `Bearer ${token}`,
          'X-File-Id': fileId || '',
          'X-Chunk-Index': chunkIndex.toString(),
          'X-Total-Chunks': totalChunks.toString(),
          'X-File-Name': fileName,
        };

        // Add study UID if we have one from previous uploads
        if (existingStudyUID) {
          headers['X-Study-Instance-UID'] = existingStudyUID;
        }

        // Retry logic for chunk upload
        let retries = 0;
        const maxRetries = 3;
        let uploadSuccess = false;
        let responseData = null;

        while (retries < maxRetries && !uploadSuccess) {
          try {
            // Upload the chunk
            const response = await axios.put(
              `${LocalAPIUpload}/direct_upload/${encodeURIComponent(chunkFileName)}`,
              chunk,
              {
                headers,
                onUploadProgress: (progressEvent: any) => {
                  const chunkProgress = progressEvent.loaded / progressEvent.total;
                  const overallProgress = ((chunkIndex + chunkProgress) / totalChunks) * 100;
                  setUploadProgress(Math.round(overallProgress));
                },
              }
            );

            // If this is the first chunk, get the file ID from the response
            if (chunkIndex === 0 && response.data && response.data.upload_id) {
              fileId = response.data.upload_id;
            }

            // Store response data to check for studyUID later
            responseData = response.data;

            uploadSuccess = true;
          } catch (error: any) {
            retries++;
            if (retries >= maxRetries) {
              throw error;
            }

            // Exponential backoff before retry
            await new Promise(resolve => setTimeout(resolve, 1000 * Math.pow(2, retries)));
            console.warn(
              `Retrying chunk ${chunkIndex + 1}/${totalChunks} (attempt ${retries + 1})...`
            );
          }
        }

        // Update progress after each chunk
        setUploadProgress(Math.round(((chunkIndex + 1) / totalChunks) * 100));
      }

      // Optional: After all chunks are uploaded, tell the server to combine them
      if (totalChunks > 1) {
        try {
          const token = localStorage.getItem('token');
          const mergeResponse = await axios.post(
            `${LocalAPIUpload}/merge_uploads`,
            {
              upload_ids: [fileId],
              original_filename: fileName,
              study_instance_uid: existingStudyUID || undefined,
            },
            {
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
              },
            }
          );

          // If this is the first file and we got a StudyInstanceUID back from merge, return it
          if (index === 0 && mergeResponse.data && mergeResponse.data.studyUID) {
            return mergeResponse.data.studyUID;
          }
        } catch (mergeError) {
          console.warn('Error merging chunks, the server may do this automatically:', mergeError);
          // Continue even if merge request fails, as some servers handle this automatically
        }
      }

      return true;
    } catch (error: any) {
      console.error(`Error uploading file ${fileName}:`, error);
      setUploadError(`Error uploading ${fileName}: ${error.message || 'Unknown error'}`);
      return false;
    }
  };

  // Function to upload using the traditional approach with batches
  const uploadToBatches = async (files: File[]) => {
    const BATCH_SIZE = 50; // Upload 50 files at a time
    const batches = [];

    // Split files into batches
    for (let i = 0; i < files.length; i += BATCH_SIZE) {
      batches.push(files.slice(i, i + BATCH_SIZE));
    }

    let uploadSuccessCount = 0;
    let uploadFailedCount = 0;
    let studyInstanceUID = '';

    // Process each batch
    for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
      const batch = batches[batchIndex];
      setUploadCurrent(batchIndex * BATCH_SIZE + 1);
      setUploadTotal(files.length);

      try {
        const token = localStorage.getItem('token');
        const formData = new FormData();

        // Add each file in this batch to the form data
        batch.forEach(file => formData.append('files', file));

        // If we have a Study Instance UID from a previous batch, send it to ensure files go to the same study
        // Add it both as a form field and as a header for flexibility with different API implementations
        const headers: Record<string, string> = {
          Authorization: `Bearer ${token}`,
        };

        if (studyInstanceUID) {
          formData.append('studyInstanceUID', studyInstanceUID);
          headers['X-Study-Instance-UID'] = studyInstanceUID;
        }

        // Upload the batch with a timeout to prevent hanging requests
        const response = await axios.post(`${LocalAPIUpload}/apiupload`, formData, {
          headers,
          timeout: 600000, // 10-minute timeout for larger batches
          onUploadProgress: (progressEvent: any) => {
            const batchProgress = progressEvent.loaded / progressEvent.total;
            const overallProgress = ((batchIndex + batchProgress) / batches.length) * 100;
            setUploadProgress(Math.round(overallProgress));
          },
        });

        // If this is the first batch, save the Study Instance UID for subsequent batches
        if (batchIndex === 0 && response.data) {
          // Try to get the directory from the response
          if (response.data.directory) {
            studyInstanceUID = response.data.directory;
          }
          // Or try studyUID field if available
          else if (response.data.studyUID) {
            studyInstanceUID = response.data.studyUID;
          }

          if (studyInstanceUID) {
            console.log(`Using Study Instance UID: ${studyInstanceUID} for all uploads`);
          }
        }

        uploadSuccessCount += batch.length;
      } catch (error: any) {
        console.error(`Error uploading batch ${batchIndex}:`, error);

        // If we got a 413 error, reduce batch size and try again
        if (error.response && error.response.status === 413) {
          const halfBatch = Math.ceil(batch.length / 2);
          console.log(`413 error, reducing batch size to ${halfBatch}`);

          // Split this batch in half and try again with smaller batches
          const firstHalf = batch.slice(0, halfBatch);
          const secondHalf = batch.slice(halfBatch);

          try {
            // Try first half
            const formData1 = new FormData();
            firstHalf.forEach(file => formData1.append('files', file));

            const headers1: Record<string, string> = {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            };

            if (studyInstanceUID) {
              formData1.append('studyInstanceUID', studyInstanceUID);
              headers1['X-Study-Instance-UID'] = studyInstanceUID;
            }

            const response1 = await axios.post(`${LocalAPIUpload}/apiupload`, formData1, {
              headers: headers1,
              timeout: 300000,
            });

            // Save study UID if this is the first successful upload
            if (!studyInstanceUID && response1.data) {
              if (response1.data.directory) {
                studyInstanceUID = response1.data.directory;
              } else if (response1.data.studyUID) {
                studyInstanceUID = response1.data.studyUID;
              }
            }

            uploadSuccessCount += firstHalf.length;

            // Try second half
            const formData2 = new FormData();
            secondHalf.forEach(file => formData2.append('files', file));

            const headers2: Record<string, string> = {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            };

            if (studyInstanceUID) {
              formData2.append('studyInstanceUID', studyInstanceUID);
              headers2['X-Study-Instance-UID'] = studyInstanceUID;
            }

            await axios.post(`${LocalAPIUpload}/apiupload`, formData2, {
              headers: headers2,
              timeout: 300000,
            });

            uploadSuccessCount += secondHalf.length;
          } catch (splitError: any) {
            console.error('Error even with smaller batches:', splitError);
            uploadFailedCount += batch.length;
            setUploadError(`Error uploading: ${splitError.message || 'Unknown error'}`);
          }
        } else {
          uploadFailedCount += batch.length;
          setUploadError(`Error uploading batch: ${error.message || 'Unknown error'}`);
        }
      }

      // Update progress after each batch
      setUploadProgress(Math.round(((batchIndex + 1) / batches.length) * 100));
    }

    return { uploadSuccessCount, uploadFailedCount };
  };

  // Main function to handle file uploads
  const uploadToServerFS = async (files: File[]) => {
    setDropInitiated(true);
    setUploadProgress(0);
    setUploadTotal(files.length);
    setUploadCurrent(0);
    setUploadError('');

    let uploadSuccessCount = 0;
    let uploadFailedCount = 0;
    let studyInstanceUID = '';

    // First, check if we can use the direct upload endpoint
    let canUseDirectUpload = false;
    try {
      // Test if the direct_upload endpoint exists with an OPTIONS request
      await axios.options(`${LocalAPIUpload}/direct_upload/test`);
      canUseDirectUpload = true;
    } catch (error) {
      console.log('Direct upload endpoint not available, using fallback method');
      canUseDirectUpload = false;
    }

    try {
      if (canUseDirectUpload) {
        // Process files sequentially to avoid overwhelming the server
        for (let i = 0; i < files.length; i++) {
          const success = await uploadFileInChunks(files[i], i, files.length, studyInstanceUID);

          // If this is the first successful upload and we got a study UID back
          if (success && success !== true && !studyInstanceUID) {
            studyInstanceUID = success;
            console.log(`Using Study Instance UID from direct upload: ${studyInstanceUID}`);
          }

          if (success) {
            uploadSuccessCount++;
          } else {
            uploadFailedCount++;
            // Continue with next file even if one fails
            console.warn(`Skipping failed file: ${files[i].name}`);
          }
        }
      } else {
        // Use batch upload approach
        const result = await uploadToBatches(files);
        uploadSuccessCount = result.uploadSuccessCount;
        uploadFailedCount = result.uploadFailedCount;
      }

      // All files processed
      if (uploadFailedCount > 0) {
        setUploadError(
          `Upload completed with ${uploadSuccessCount} successful and ${uploadFailedCount} failed files.`
        );
      }

      // If we have some successful uploads, consider it a success
      if (uploadSuccessCount > 0) {
        setUploadProgress(100);
        // Wait a moment to show 100% before resetting the UI
        setTimeout(() => {
          setDropInitiated(false);
        }, 1000);

        // Call API endpoint when upload is completed successfully
        try {
          const token = window.localStorage.getItem('token');
          const response = await fetch(`${LocalAPI}/apilocal/v1/local?skip=true`, {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });

          await response.json();
        } catch (apiError) {
          console.error('Error calling API after upload:', apiError);
          // Don't set upload error since the upload itself was successful
        }
      } else {
        setDropInitiated(false);
      }
    } catch (error: any) {
      console.error('Error in upload process:', error);
      setUploadError(`Upload failed: ${error.message || 'Unknown error'}`);
      setDropInitiated(false);
    }
  };

  const onDrop = async (acceptedFiles: File[]) => {
    if (serverFS) {
      console.log(`Uploading ${acceptedFiles.length} files to server's local storage`);
      uploadToServerFS(acceptedFiles);
    } else {
      try {
        // @ts-ignore: filesToStudies may require the dataSource parameter
        const studies = await filesToStudies(acceptedFiles, dataSource);

        const query = new URLSearchParams();

        if (microscopyExtensionLoaded) {
          const smStudies = studies.filter(id => {
            const study = DicomMetadataStore.getStudy(id);
            return (
              study.series.findIndex(
                s => s.Modality === 'SM' || s.instances[0].Modality === 'SM'
              ) >= 0
            );
          });

          if (smStudies.length > 0) {
            smStudies.forEach(id => query.append('StudyInstanceUIDs', id));

            modePath = 'microscopy';
          }
        }

        studies.forEach(id => query.append('StudyInstanceUIDs', id));
        query.append('datasources', 'dicomlocal');

        // sessionStorage.setItem('isDicomLocal', 'true');
        navigate(`/${modePath}?${decodeURIComponent(query.toString())}`);
      } catch (error) {
        console.error('Error in onDrop:', error);
        setUploadError('Error in onDrop');
      }
    }
  };

  useEffect(() => {
    const user_data = JSON.parse(localStorage.getItem('user_data'));
    const is_demo = user_data && user_data.email == 'userdemo@gmail.com';
    const is_share = JSON.parse(localStorage.getItem('share'));
    const share_case =
      user_data &&
        user_data.isShared &&
        is_share &&
        user_data.isShared == 'true' &&
        is_share == true
        ? true
        : false;

    if (share_case == true || is_demo == true) {
      navigate('/sign-in');
    }
  }, []);

  // Set body style
  useEffect(() => {
    document.body.classList.add('bg-black');
    return () => {
      document.body.classList.remove('bg-black');
    };
  }, []);

  return (
    <Dropzone
      ref={dropzoneRef}
      onDrop={acceptedFiles => {
        setDropInitiated(true);
        onDrop(acceptedFiles);
      }}
      noClick
    >
      {({ getRootProps, getInputProps, open }) => (
        <div
          {...getRootProps()}
          style={{ width: '100%', height: '100%' }}
        >
          <div className="flex h-screen w-screen items-center justify-center">
            <div className="from-primary-modalgray to-primary-modalgradient border-primary-darkgray mx-auto flex flex-col items-center justify-center space-y-2 rounded-xl bg-gradient-to-tr py-8 px-8 drop-shadow-md">
              {dropInitiated ? (
                <div className="flex flex-col items-center justify-center pt-48">
                  <LoadingIndicatorProgress
                    className={'h-full w-full bg-black'}
                    isUpload={true}
                    textBlock={
                      serverFS
                        ? `Uploading file ${uploadCurrent} of ${uploadTotal} (${uploadProgress}%)`
                        : 'Uploading...'
                    }
                    progress={uploadProgress || 0}
                  />
                  {uploadError && (
                    <div className="mt-4 max-w-md text-center text-red-500">{uploadError}</div>
                  )}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center">
                  <div className="border-primary-gray text-primary-gray flex items-center rounded-3xl border-[1px] px-3 text-sm">
                    Upload Files Locally
                  </div>
                  <div className="text-primary-gray pt-5 text-lg font-bold">
                    Drag and Drop DICOM files here to load them in the Viewer
                  </div>
                  <div className="flex flex-col pt-5 md:flex-row">
                    <div
                      className="border-primary-gray hover:border-primary-darkgray mr-6 h-60 w-60 cursor-pointer rounded-xl border-[1px] transition duration-300"
                      onClick={open}
                    >
                      <div className="flex flex-col items-center justify-center pt-10">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={0.5}
                          stroke="white"
                          className="h-24 w-24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
                          />
                        </svg>
                        <span className="text-primary-gray pt-5">Click Or Drop to load files</span>
                      </div>
                      <input {...(getInputProps() as any)} />
                    </div>
                    <div
                      className="border-primary-gray hover:border-primary-darkgray h-60 w-60 cursor-pointer rounded-xl border-[1px] transition duration-300"
                      onClick={open}
                    >
                      <div className="flex flex-col items-center justify-center pt-10">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={0.5}
                          stroke="white"
                          className="h-24 w-24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M3.75 9.776c.112-.017.227-.026.344-.026h15.812c.117 0 .232.009.344.026m-16.5 0a2.25 2.25 0 00-1.883 2.542l.857 6a2.25 2.25 0 002.227 1.932H19.05a2.25 2.25 0 002.227-1.932l.857-6a2.25 2.25 0 00-1.883-2.542m-16.5 0V6A2.25 2.25 0 016 3.75h3.879a1.5 1.5 0 011.06.44l2.122 2.12a1.5 1.5 0 001.06.44H18A2.25 2.25 0 0120.25 9v.776"
                          />
                        </svg>
                        <span className="text-primary-gray pt-5">
                          Click Or Drop to load folders
                        </span>
                      </div>
                      <input
                        {...(getInputProps() as any)}
                        webkitdirectory="true"
                        mozdirectory="true"
                      />
                    </div>
                  </div>
                  <div className="flex gap-x-3">
                    {CLIENT_NAME !== 'demo-local-fs' && (
                      <div
                        onClick={() => setServerFS(!serverFS)}
                        className={`${serverFS == true ? 'bg-primary-newgray' : ''} border-primary-gray hover:border-primary-darkgray text-primary-gray mt-5 flex cursor-pointer items-center rounded-3xl border-[1px] px-4 py-1 text-sm transition duration-200`}
                      >
                        Upload to Server's Local
                      </div>
                    )}

                    <div
                      onClick={() => navigate('/')}
                      className="border-primary-gray hover:border-primary-darkgray text-primary-gray mt-5 flex cursor-pointer items-center rounded-3xl border-[1px] px-4 py-1 text-sm transition duration-200"
                    >
                      Back
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </Dropzone>
  );
}

export default Local;
